import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useOrg,
  useRegister,
  useSaveRegister,
  useSeasons,
  useTeams,
} from '@/app/queries';
import { useSession as useAuthSession } from '@/app/session';
import { formatDate, sheetName } from '@/lib/format';
import { ATTENDANCE_MARK as MARK, monthBounds, registerColumns } from '@/lib/attendanceSheet';
import { registerReport } from '@/reports/build';
import { ReportView } from '@/reports/ReportView';
import { exportReportPdf, reportFilename } from '@/reports/export';
import type { BuildContext } from '@/reports/build';
import { todayISO } from '@shared/dates';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextInput } from '@/ui/Field';
import { MATRIX_BREAKPOINT, useAtLeast } from '@/ui/Matrix';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { PaperPreview } from '@/ui/PaperPreview';

/** The order a box cycles through when tapped. Blank last, so it is reachable. */
const CYCLE = ['present', 'absent', 'late', 'excused', 'injured', ''] as const;

/** The same statuses, offered all at once where there is room for them. */
const MARKABLE = CYCLE.filter((s): s is Exclude<typeof CYCLE[number], ''> => s !== '');

const TONE: Record<string, string> = {
  present: 'bg-ok/15 text-ok',
  absent: 'bg-risk/12 text-risk',
  late: 'bg-signal/15 text-signal',
  excused: 'bg-surface-sunken text-ink-muted',
  injured: 'bg-warn/15 text-warn',
  modified: 'bg-surface-sunken text-ink-muted',
};

/**
 * The register, both ways round.
 *
 * Print it, carry it, mark it on a clipboard — then bring it back by reading it
 * off the photo into the same grid. The sheet on screen and the sheet on paper
 * are the same rows in the same order, which is the only thing that makes
 * copying one into the other bearable.
 */
export function Register({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const org = useOrg();
  const { user } = useAuthSession();

  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;
  const seasons = useSeasons(activeTeamId ?? undefined);

  const now = new Date();
  const [period, setPeriod] = useState(() => monthBounds(now.getUTCFullYear(), now.getUTCMonth()));
  const register = useRegister(activeTeamId, period.from, period.to);
  const save = useSaveRegister(activeTeamId ?? '');

  const [marks, setMarks] = useState<Record<string, string>>({});
  const [createSessions, setCreateSessions] = useState(true);
  const [sessionTitle, setSessionTitle] = useState('Training');
  const [result, setResult] = useState<{ saved: number; created: number; skipped: number } | null>(null);
  const [scan, setScan] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(
    () =>
      registerColumns(
        period.from,
        period.to,
        locale,
        (register.data?.sessions ?? []).map((s) => s.onDate),
        (register.data?.matches ?? []).map((m) => m.onDate),
      ),
    [period.from, period.to, locale, register.data?.sessions, register.data?.matches],
  );

  const squad = register.data?.squad ?? [];

  /** Who the fixture on a date was against, for the column's tooltip. */
  const opponentOn = (date: string) =>
    (register.data?.matches ?? []).find((m) => m.onDate === date)?.opponent ?? null;

  // Whatever is already recorded starts in the grid, so a coach sees what they
  // are about to overwrite rather than discovering it afterwards.
  useEffect(() => {
    const next: Record<string, string> = {};
    for (const row of register.data?.recorded ?? []) {
      next[`${row.playerId}|${row.date}`] = row.status;
    }
    setMarks(next);
    setResult(null);
  }, [register.data]);

  const wide = useAtLeast(MATRIX_BREAKPOINT);

  const cycle = (playerId: string, date: string) => {
    const key = `${playerId}|${date}`;
    setMarks((current) => {
      const at = CYCLE.indexOf((current[key] ?? '') as (typeof CYCLE)[number]);
      const next = CYCLE[(at + 1) % CYCLE.length]!;
      const updated = { ...current };
      if (next === '') delete updated[key];
      else updated[key] = next;
      return updated;
    });
  };

  /**
   * Set one mark outright.
   *
   * The grid cycles because a 24px box has room for one gesture and nothing
   * else. With a phone's width there is room for every status at once, which is
   * one tap instead of up to five — and it is what the pitch-side register on
   * `Run` already does for the same question.
   */
  const setMark = (playerId: string, date: string, status: string) => {
    const key = `${playerId}|${date}`;
    setMarks((current) => {
      const updated = { ...current };
      if (!status || current[key] === status) delete updated[key];
      else updated[key] = status;
      return updated;
    });
  };

  const fillColumn = (date: string, status: string) => {
    setMarks((current) => {
      const updated = { ...current };
      for (const p of squad) {
        const key = `${p.id}|${date}`;
        if (status === '') delete updated[key];
        else updated[key] = status;
      }
      return updated;
    });
  };

  const ctx: BuildContext = useMemo(
    () => ({
      locale,
      t: t as BuildContext['t'],
      meta: {
        clubName: org.data?.name ?? '',
        teamName: teamList.find((x) => x.id === activeTeamId)?.name,
        coachName: user?.name,
        seasonLabel: (seasons.data ?? []).find((s) => s.status === 'active')?.label,
        onDate: formatDate(todayISO(), locale),
        logoUrl: org.data?.logoKey
          ? `/api/org/logo?v=${encodeURIComponent(org.data.logoKey)}`
          : null,
        accent: org.data?.primaryColor,
        footer: org.data?.reportFooter,
      },
    }),
    [locale, t, org.data, teamList, activeTeamId, seasons.data, user?.name],
  );

  const report = useMemo(
    () =>
      registerReport(
        ctx,
        squad.map((p) => ({
          id: p.id,
          name: `${p.knownAs ?? p.firstName} ${p.lastName}`,
          shirtNumber: p.shirtNumber,
          positionKey: p.positionKey,
        })),
        columns,
        period,
        marks,
      ),
    [ctx, squad, columns, period, marks],
  );

  const runExport = async () => {
    if (!sheetRef.current) return;
    setExporting(true);
    setExportError(null);
    try {
      await exportReportPdf(
        sheetRef.current,
        reportFilename([report.meta.title, report.meta.teamName], period.from),
      );
    } catch {
      setExportError(t('report.failed'));
    } finally {
      setExporting(false);
    }
  };

  const commit = async () => {
    const cells = Object.entries(marks).map(([key, status]) => {
      const [playerId, date] = key.split('|');
      return { playerId: playerId!, date: date!, status };
    });
    const r = await save.mutateAsync({ cells, createMissingSessions: createSessions, sessionTitle });
    setResult({ saved: r.saved, created: r.sessionsCreated, skipped: r.skippedDates.length });
  };

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
        title={t('register.title')}
        lead={t('register.lead')}
        help={localePath(locale, '/guide/run')}
      />
        <EmptyState title={t('development.noTeam')} body={t('development.noTeamBody')} />
      </>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('register.title')}
        lead={t('register.lead')}
        help={localePath(locale, '/guide/run')}
      />

      <div className="grid gap-5">
        <Panel
          title={t('register.period')}
          action={
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => window.print()}>
                {t('report.print')}
              </Button>
              <Button variant="primary" busy={exporting} onClick={() => void runExport()}>
                {exporting ? t('report.preparing') : t('register.print')}
              </Button>
            </div>
          }
        >
          <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-4">
            {teamList.length > 1 ? (
              <Field label={t('nav.team')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={activeTeamId ?? ''}
                    onChange={(e) => setTeamId(e.target.value)}
                  >
                    {teamList.map((tm) => (
                      <option key={tm.id} value={tm.id}>
                        {tm.name}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
            ) : null}

            <Field label={t('register.month')}>
              {(p) => (
                <TextInput
                  {...p}
                  type="month"
                  value={period.from.slice(0, 7)}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split('-').map(Number);
                    if (!y || !m) return;
                    setPeriod(monthBounds(y, m - 1));
                  }}
                />
              )}
            </Field>

            <Field label={t('register.from')}>
              {(p) => (
                <TextInput
                  {...p}
                  type="date"
                  value={period.from}
                  onChange={(e) => setPeriod((x) => ({ ...x, from: e.target.value }))}
                />
              )}
            </Field>

            <Field label={t('register.to')}>
              {(p) => (
                <TextInput
                  {...p}
                  type="date"
                  value={period.to}
                  onChange={(e) => setPeriod((x) => ({ ...x, to: e.target.value }))}
                />
              )}
            </Field>
          </div>

          {exportError ? (
            <div className="px-4 pb-4">
              <Notice tone="risk">{exportError}</Notice>
            </div>
          ) : null}
        </Panel>

        {/* The printable sheet. Hidden on screen behind the grid, but it is the
            node the exporter captures and the one `window.print()` leaves. */}
        {/* A landscape register is 1123px. Scaled to fit, the coach can see the
            sheet he is about to print; the sheet itself is untouched, which is
            what keeps D-039's one-page arithmetic true on paper. */}
        <PaperPreview className="report-preview">
          <div ref={sheetRef} className="mx-auto w-fit shadow-(--shadow-sheet)">
            <ReportView report={report} />
          </div>
        </PaperPreview>

        {/* Reached for when a paper sheet comes back, not every visit. */}
        <Panel
          title={t('register.scan')}
          collapsible
          storeKey="register.scan"
          defaultOpen={false}
          summary={t('register.scanHint')}
        >
          <div className="grid gap-3 p-4">
            <p className="max-w-[72ch] text-meta text-ink-muted">{t('register.scanHint')}</p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileInput}
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setScan(URL.createObjectURL(file));
                }}
              />
              <Button variant="secondary" onClick={() => fileInput.current?.click()}>
                {t('register.sheetFile')}
              </Button>
              {scan ? (
                <Button
                  variant="quiet"
                  onClick={() => {
                    URL.revokeObjectURL(scan);
                    setScan(null);
                  }}
                >
                  {t('register.removeSheet')}
                </Button>
              ) : null}
            </div>
            <Notice>{t('register.noAiYet')}</Notice>
          </div>
        </Panel>

        {scan ? (
          <Panel title={t('register.sheetFile')}>
            <div className="max-h-[70vh] overflow-auto p-2">
              <img src={scan} alt={t('register.sheetFile')} className="mx-auto max-w-full" />
            </div>
          </Panel>
        ) : null}

        <Panel title={t('register.grid')} count={squad.length || undefined}>
          {register.isLoading ? (
            <p className="flex items-center gap-2 px-4 py-6 text-ink-muted">
              <Spinner /> {t('common.loading')}
            </p>
          ) : squad.length === 0 ? (
            <EmptyState title={t('register.empty')} body={t('register.emptyBody')} />
          ) : (
            <>
              <p className="px-4 pt-3 text-meta text-ink-muted">
                {wide ? t('register.cycleHint') : t('register.dayHint')}
              </p>
              {!wide ? (
                <DayRegister
                  columns={columns}
                  squad={squad}
                  marks={marks}
                  locale={locale}
                  onSet={setMark}
                  onFillDay={(date) => fillColumn(date, 'present')}
                  opponentOn={opponentOn}
                />
              ) : (
              <div className="overflow-x-auto p-4">
                <table className="border-collapse text-meta">
                  <thead>
                    <tr>
                      <th className="sticky start-0 z-10 bg-surface px-2 py-1 text-start font-normal text-ink-faint">
                        {t('squad.player')}
                      </th>
                      {columns.map((c) => (
                        <th
                          key={c.date}
                          scope="col"
                          className={`px-0.5 py-1 text-center font-normal ${
                            c.isWeekend ? 'text-ink-faint' : 'text-ink-muted'
                          }`}
                        >
                          <button
                            type="button"
                            title={
                              c.kind === 'match'
                                ? `${t('register.matchDay')} ${opponentOn(c.date) ?? ''}`.trim()
                                : t('register.fillColumn')
                            }
                            onClick={() => fillColumn(c.date, 'present')}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              fillColumn(c.date, '');
                            }}
                            className={`tabular block w-6 rounded-[2px] leading-tight hover:bg-surface-sunken ${
                              c.scheduled ? 'font-semibold text-ink' : ''
                            } ${c.kind === 'match' ? 'bg-signal-soft' : ''}`}
                          >
                            <span className="block">{c.day}</span>
                            <span className="block text-[0.625rem] opacity-70">{c.weekday}</span>
                            {/* A match day is a different kind of absence. */}
                            <span className="block text-[0.5rem] font-bold text-signal">
                              {c.kind === 'match' ? t('register.matchShort') : '\u00a0'}
                            </span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {squad.map((p) => (
                      <tr key={p.id}>
                        <th
                          scope="row"
                          className="sticky start-0 z-10 whitespace-nowrap bg-surface px-2 py-1 text-start font-medium"
                        >
                          <span className="tabular me-2 text-ink-faint">{p.shirtNumber ?? '–'}</span>
                          {sheetName(p.knownAs ?? p.firstName, p.lastName)}
                        </th>
                        {columns.map((c) => {
                          const status = marks[`${p.id}|${c.date}`] ?? '';
                          return (
                            <td key={c.date} className="p-0.5">
                              <button
                                type="button"
                                onClick={() => cycle(p.id, c.date)}
                                aria-label={`${sheetName(p.firstName, p.lastName)} ${c.date} ${
                                  status ? t(`register.status.${status}`) : t('register.status.none')
                                }`}
                                className={`grid size-6 place-items-center rounded-[3px] border border-line text-[0.6875rem] font-semibold transition-colors ${
                                  status ? TONE[status] : 'bg-surface text-ink-faint hover:border-ink'
                                } ${c.isWeekend && !status ? 'bg-surface-sunken' : ''}`}
                              >
                                {MARK[status] ?? ''}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}

              <div className="grid gap-3 border-t border-line p-4">
                <label className="flex items-start gap-2 text-meta text-ink-muted">
                  <input
                    type="checkbox"
                    checked={createSessions}
                    onChange={(e) => setCreateSessions(e.target.checked)}
                    className="mt-0.5 size-4 accent-ink"
                  />
                  <span>
                    {t('register.createSessions')}
                    <span className="block text-ink-faint">{t('register.createSessionsHint')}</span>
                  </span>
                </label>

                {createSessions ? (
                  <div className="max-w-xs">
                    <Field label={t('register.sessionTitle')}>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={sessionTitle}
                          onChange={(e) => setSessionTitle(e.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="primary"
                    busy={save.isPending}
                    disabled={Object.keys(marks).length === 0}
                    onClick={() => void commit()}
                  >
                    {t('register.save')}
                  </Button>
                  {result ? (
                    <span className="text-meta text-ink-muted">
                      {t('register.saved', { count: result.saved })}
                      {result.created > 0
                        ? ` · ${t('register.sessionsCreated', { count: result.created })}`
                        : ''}
                      {result.skipped > 0
                        ? ` · ${t('register.skipped', { count: result.skipped })}`
                        : ''}
                    </span>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

/**
 * The register, one day at a time.
 *
 * A month is thirty-one columns. There is no arrangement of thirty-one columns
 * that fits a 390px screen, and the attempt was 24px boxes — the smallest
 * controls in the product, 775 of them, on the screen a coach uses standing at
 * the side of a pitch in the rain.
 *
 * So the phone asks the question a coach is actually holding: who is here
 * today. The month is still there on a wider screen, and on the printed sheet
 * where thirty-one columns belong (D-039). Nothing about what is stored
 * changes — this writes the same marks into the same grid.
 */
function DayRegister({
  columns,
  squad,
  marks,
  locale,
  onSet,
  onFillDay,
  opponentOn,
}: {
  columns: readonly { date: string; day: number; weekday: string; scheduled: boolean; kind?: string | null }[];
  squad: readonly { id: string; firstName: string; lastName: string; knownAs?: string | null; shirtNumber?: number | null }[];
  marks: Record<string, string>;
  locale: Locale;
  onSet: (playerId: string, date: string, status: string) => void;
  onFillDay: (date: string) => void;
  opponentOn: (date: string) => string | null | undefined;
}) {
  const t = useTranslations();
  const today = todayISO();

  // Open on today when the period contains it, otherwise the first day that has
  // something on it — a coach marking a register is marking a day that happened.
  const initial = useMemo(() => {
    const onToday = columns.findIndex((c) => c.date === today);
    if (onToday >= 0) return onToday;
    const scheduled = columns.findIndex((c) => c.scheduled);
    return scheduled >= 0 ? scheduled : 0;
  }, [columns, today]);

  const [at, setAt] = useState(initial);
  useEffect(() => setAt(initial), [initial]);

  const day = columns[Math.min(at, columns.length - 1)];
  if (!day) return <p className="px-4 py-6 text-meta text-ink-muted">{t('register.noScheduled')}</p>;

  const marked = squad.filter((p) => marks[`${p.id}|${day.date}`]).length;

  return (
    <div className="grid gap-3 p-4">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          aria-label={t('common.previousDay')}
          className="!min-w-11 !px-3"
          disabled={at === 0}
          onClick={() => setAt((i) => Math.max(0, i - 1))}
        >
          <span aria-hidden="true">‹</span>
        </Button>
        <span className="min-w-0 flex-1 text-center">
          <span className="block font-condensed text-subhead font-semibold">
            {formatDate(day.date, locale)}
          </span>
          <span className="block text-meta text-ink-faint">
            {day.kind === 'match'
              ? `${t('register.matchDay')} ${opponentOn(day.date) ?? ''}`.trim()
              : t('register.rate', { rate: Math.round((marked / Math.max(1, squad.length)) * 100) })}
          </span>
        </span>
        <Button
          variant="secondary"
          aria-label={t('common.nextDay')}
          className="!min-w-11 !px-3"
          disabled={at >= columns.length - 1}
          onClick={() => setAt((i) => Math.min(columns.length - 1, i + 1))}
        >
          <span aria-hidden="true">›</span>
        </Button>
      </div>

      <Button variant="secondary" className="!text-meta" onClick={() => onFillDay(day.date)}>
        {t('register.fillColumn')}
      </Button>

      <ul className="divide-y divide-line border-t border-line">
        {squad.map((p) => {
          const status = marks[`${p.id}|${day.date}`] ?? '';
          return (
            <li key={p.id} className="py-2.5">
              <span className="flex items-baseline gap-2">
                <span className="tabular w-6 shrink-0 text-ink-faint">{p.shirtNumber ?? '–'}</span>
                <span className="min-w-0 flex-1 truncate font-medium">
                  {sheetName(p.knownAs ?? p.firstName, p.lastName)}
                </span>
              </span>
              {/* No indent: 32px of it pushed the fifth status onto a second
                  line, which doubled every player's height down a squad of
                  twenty-four. */}
              <span className="mt-1.5 flex flex-wrap gap-1">
                {MARKABLE.map((k) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={status === k}
                    onClick={() => onSet(p.id, day.date, k)}
                    className={`min-h-11 min-w-11 flex-1 rounded-(--radius-control) border px-2 text-meta transition-colors ${
                      status === k
                        ? 'border-ink bg-ink text-paper'
                        : 'border-line bg-surface text-ink-faint hover:border-ink hover:text-ink'
                    }`}
                  >
                    {t(`register.status.${k}`)}
                  </button>
                ))}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
